
import { FormGroup, FormControl, TextAreaInput, NumberInput, TypeChecker, TextInput, DOMHelper, BoolHelper, Dropdown } from "@rivtn/noseur";
import { i18nManager } from "../../../../i18n";
import { ConfigurationType } from "../../../../services";
import { Schemes } from "../../../../utils";
import React from "react";

export interface ConfigurationValueProps {
    value: any;
    type: ConfigurationType;
};

export function ConfigurationValue(props: ConfigurationValueProps) {

    const { type, value } = props;

    if (type === ConfigurationType.LIST || type === ConfigurationType.OPTIONS) {
        return buildListEntry(value, true);
    } else if (type === ConfigurationType.MAP || type === ConfigurationType.LIST_OF_MAP) {
        return buildMapEntry(value, true);
    }
    return (<span>{value}</span>);

    function buildListEntry(entry: any, og?: boolean) {
        return (<ul style={og ? { marginTop: 15 } : undefined}>
            {entry.map((v: any) => (<li key={v}>{v}</li>))}
        </ul>);
    }

    function buildMapEntry(entry: any, og?: boolean) {
        return (<ul style={og ? { marginTop: 15 } : undefined}>
            {Object.keys(entry).map((v: any) => {
                let valueComponent;
                const entryValue = entry[v];
                if (TypeChecker.isObject(entryValue)) {
                    valueComponent = buildMapEntry(entryValue);
                } else {
                    console.log("THE VVV", entry, entryValue);
                    valueComponent = (<span style={{ opacity: 0.5 }}>- {entryValue}</span>);
                }
                return (<li key={v}>
                    <span>{v} </span>
                    {valueComponent}
                </li>);
            })}
        </ul>);
    }
};

export interface ConfigurationInputsProps {
    value?: any;
    type?: ConfigurationType;
    excludes?: ConfigurationType[];
    onNewValue: (value: any) => void;
    changeProps: (setType: (type: ConfigurationType, value?: any) => void) => void;
};

export function ConfigurationInputs(props: ConfigurationInputsProps) {

    const labels = i18nManager.Labels.dashboard.ploutous.configurations.type_info as any;
    const { type: _type = "TEXT", value: _value, onNewValue, excludes = [] } = props;
    const [value, setValue] = React.useState(_value);
    const [type, setType] = React.useState(_type);
    props.changeProps((t, v) => {
        if (t) setType(t);
        if (v) setValue(v);
    });

    React.useEffect(() => {
        if (BoolHelper.equalsAny(type, [ConfigurationType.OPTIONS, ConfigurationType.LIST, ConfigurationType.LIST_OF_NUMBER, ConfigurationType.LIST_OF_TEXT]) && !TypeChecker.isArray(value)) {
            let newValue = TypeChecker.isObject(value) ? Object.values(value) : [`${value ?? ""}`];
            setValue(newValue);
            onNewValue(newValue);
        } else if (BoolHelper.equalsAny(type, [ConfigurationType.MAP]) && !TypeChecker.isArray(value)) {
            let newValue = TypeChecker.isObject(value) ? value : { field1: value };
            setValue(newValue);
            onNewValue(newValue);
        }
    }, [type]);

    if (BoolHelper.equalsAny(type, excludes)) {
        return undefined;
    }

    return (<FormGroup className="form" scheme={Schemes.RIVTN_QUIRINUS} style={{ display: "flex", flexDirection: "column" }}>
        <span>{labels?.[`enter_${type.toLowerCase()}`]}</span>
        {(type === ConfigurationType.TEXT) && (<FormControl validStyle={{ borderColor: "transparent" }} contentStyle={{ borderRadius: 0 }}>
            <TextAreaInput fill noStyle defaultValue={value} onInputComplete={onNewValue} placeholder={labels?.[`placeholder_${type.toLowerCase()}`]} />
        </FormControl>)}
        {(type === ConfigurationType.NUMBER) && (<FormControl validStyle={{ borderColor: "transparent" }} contentStyle={{ borderRadius: 0 }}>
            <NumberInput fill noStyle defaultValue={value} onInputComplete={onNewValue} placeholder={labels?.[`placeholder_${type.toLowerCase()}`]} />
        </FormControl>)}
        {(type === ConfigurationType.OPTIONS) && (<>
            {(TypeChecker.isArray(value) ? value : [`${value}`]).map((entry: string, index: number) => (<FormControl fill scheme={Schemes.RIVTN_QUIRINUS} key={DOMHelper.uniqueElementId("con-opts")}
                validStyle={{ borderColor: "transparent" }} contentStyle={{ borderRadius: 0 }}
                rightContent={<i className="fa fa-trash" style={{ color: "#EB5959", cursor: "pointer" }} onClick={() => {
                    value.splice(index, 1);
                    setValue([...value]);
                }} />}>
                <TextInput fill noStyle defaultValue={entry ?? ""} onInputComplete={(v) => {
                    value[index] = v;
                    setValue([...value]);
                    onNewValue(value);
                }} placeholder={labels?.[`placeholder_${type.toLowerCase()} ${index + 1}`]} />
            </FormControl>))}
            <span className="link" style={{ marginTop: 15, fontSize: 14 }} onClick={() => {
                setValue([...value, ""]);
            }}>{i18nManager.Labels.dashboard.ploutous.configurations.add_new_entry}</span>
        </>)}
        {(BoolHelper.equalsAny(type, [ConfigurationType.LIST, ConfigurationType.LIST_OF_TEXT, ConfigurationType.LIST_OF_NUMBER])) && (<>
            {(TypeChecker.isArray(value) ? value : [`${value}`]).map((entry: string, index: number) => (<div className="form" key={DOMHelper.uniqueElementId("con-li")} style={{ display: "flex", flexDirection: "row" }}>
                <FormControl scheme={Schemes.RIVTN_QUIRINUS} validStyle={{ borderColor: "transparent" }} contentStyle={{ borderRadius: 0 }} fill>
                    {React.createElement(type === ConfigurationType.LIST_OF_NUMBER ? NumberInput : TextInput, {
                        fill: true,
                        noStyle: true,
                        defaultValue: (entry ?? ""),
                        placeholder: labels?.[`placeholder_${type.toLowerCase()}`],
                        onInputComplete: (v) => {
                            value[index] = v;
                            setValue([...value]);
                            onNewValue(value);
                        },
                    })}
                </FormControl>
                {(type === ConfigurationType.LIST) && (<Dropdown fill scheme={Schemes.RIVTN_QUIRINUS} formControlProps={{
                    contentStyle: { borderRadius: 0 },
                    style: { maxWidth: 150, marginLeft: 15 },
                    validStyle: { borderColor: "transparent" },
                }} selectedOptionIndex={0} options={i18nManager.Labels.dashboard.ploutous.configurations.type_options.filter((f) => BoolHelper.equalsAny(f.value, ["TEXT", "NUMBER"]))} />)}
            </div>))}
            <span className="link" style={{ marginTop: 15, fontSize: 14 }} onClick={() => {
                setValue([...value, ""]);
            }}>{i18nManager.Labels.dashboard.ploutous.configurations.add_new_entry}</span>
        </>)}
        {(BoolHelper.equalsAny(type, [ConfigurationType.MAP, ConfigurationType.LIST_OF_MAP])) && (<>
            <FormControl validStyle={{ borderColor: "transparent" }} contentStyle={{ borderRadius: 0 }}>
                <TextAreaInput fill noStyle defaultValue={JSON.stringify(value, null, 2)} onInputComplete={onNewValue} placeholder={labels?.[`placeholder_${type.toLowerCase()}`]} />
            </FormControl>
        </>)}
    </FormGroup>);
};

